import {
  Button,
  Column,
  FlexContainer,
  FullscreenSheet,
  InputText,
  ScannerInput,
  SoundNotificationContext,
  Span,
  TableSimple,
  TriangleAlertIcon,
} from '@uni-space/uni-components';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getErrorMessageAsync } from '@shared/api';
import { ParcelDto } from '@shared/types/parcel';
import { checkParcelWeight, getBagParcels, getScalesWeight } from '../api';
import {
  CheckParcelWeightStatus,
  RESULT_MESSAGE_KEYS,
  WeightCheckParcelRow,
  WeightCheckRowStatus,
} from '../model/weightCheck';

type CheckResult = { tone: 'success' | 'changed' | 'error'; message: string };

const sameTrack = (a: string, b: string) => a.trim().toUpperCase() === b.trim().toUpperCase();

const isValidWeight = (weight: number) => Number.isFinite(weight) && weight > 0 && weight < 30000;

export default function WeightCheckBag(props: { bag: { id: number; number: string } | null; onClose: () => void }) {
  const { bag, onClose } = props;
  const { onSoundSuccess, onSoundError } = useContext(SoundNotificationContext);
  const { t, i18n } = useTranslation();

  // null — ещё не проверяли подключение к весам
  const [scalesConnected, setScalesConnected] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Трек, для которого весы недоступны и вес ждём руками
  const [manualTrackNumber, setManualTrackNumber] = useState<string | null>(null);
  const [manualWeight, setManualWeight] = useState('');
  const [lastResult, setLastResult] = useState<CheckResult | null>(null);
  // Результаты проверок за сессию по трек-номеру
  const [checks, setChecks] = useState<Record<string, { status: WeightCheckRowStatus; weight: number | null }>>({});
  const manualInputRef = useRef<HTMLInputElement | null>(null);

  const {
    data: parcels = [],
    isLoading: isLoadingParcels,
    refetch: refetchParcels,
  } = useQuery({
    queryKey: ['weightCheckBagParcels', bag?.id],
    queryFn: () => getBagParcels(bag!.id),
    enabled: Boolean(bag),
  });

  useEffect(() => {
    if (!bag) return;
    const controller = new AbortController();
    getScalesWeight(controller.signal).then((res) => {
      if (!controller.signal.aborted) setScalesConnected(Boolean(res));
    });
    return () => controller.abort();
  }, [bag]);

  useEffect(() => {
    if (manualTrackNumber) manualInputRef.current?.focus();
  }, [manualTrackNumber]);

  const showError = (message: string) => {
    setLastResult({ tone: 'error', message });
    onSoundError();
  };

  const close = () => {
    if (isLoading) return;
    setScalesConnected(null);
    setManualTrackNumber(null);
    setManualWeight('');
    setLastResult(null);
    setChecks({});
    onClose();
  };

  const submitWeight = async (trackNumber: string, weight: number) => {
    if (!bag) return false;
    if (!isValidWeight(weight)) {
      showError(t('weightError'));
      return false;
    }

    setIsLoading(true);
    try {
      const res = await checkParcelWeight(bag.id, trackNumber, weight);
      const message = t(RESULT_MESSAGE_KEYS[res.status], {
        trackNumber,
        bagNumber: bag.number,
        weight: res.newWeightGrams,
        previous: res.previousWeightGrams ?? '—',
      });

      if (res.status === CheckParcelWeightStatus.WeightConfirmed || res.status === CheckParcelWeightStatus.WeightChanged) {
        const changed = res.status === CheckParcelWeightStatus.WeightChanged;
        setChecks((prev) => ({ ...prev, [trackNumber]: { status: changed ? 'changed' : 'confirmed', weight } }));
        setLastResult({ tone: changed ? 'changed' : 'success', message });
        onSoundSuccess();
        return true;
      }

      setChecks((prev) => ({ ...prev, [trackNumber]: { status: 'error', weight } }));
      showError(message);
      return true;
    } catch (e) {
      showError(await getErrorMessageAsync(e, t('common.systemError.desc')));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const onScanSubmit = async (value: string) => {
    const trackNumber = value.trim();
    if (!trackNumber || isLoading || manualTrackNumber) return;

    setIsLoading(true);
    // Посылка обязана лежать в этом мешке — иначе не взвешиваем. Если в загруженном списке её нет,
    // перечитываем его: посылку могли положить в мешок уже после открытия экрана.
    const isInBag = (items: ParcelDto[]) => items.some((p) => sameTrack(p.trackNumber, trackNumber));
    if (!isInBag(parcels)) {
      const { data: fresh = [] } = await refetchParcels();
      if (!isInBag(fresh)) {
        setIsLoading(false);
        setChecks((prev) => ({ ...prev, [trackNumber]: { status: 'error', weight: null } }));
        showError(t('weightCheck.result.notInBag', { trackNumber, bagNumber: bag?.number ?? '' }));
        return;
      }
    }

    const weightResponse = await getScalesWeight();
    setIsLoading(false);

    // Весы не ответили — просим ввести вес руками для этого трека
    if (!weightResponse) {
      setScalesConnected(false);
      setManualTrackNumber(trackNumber);
      return;
    }

    setScalesConnected(true);
    if (!weightResponse.weight) {
      showError(t('consolidatorTracking.changeWeight.emptyScales'));
      return;
    }

    await submitWeight(trackNumber, weightResponse.weight);
  };

  const onManualSubmit = async () => {
    if (!manualTrackNumber || isLoading || !manualWeight) return;
    const ok = await submitWeight(manualTrackNumber, Number.parseInt(manualWeight, 10));
    if (ok) {
      setManualTrackNumber(null);
      setManualWeight('');
    }
  };

  const cancelManual = () => {
    setManualTrackNumber(null);
    setManualWeight('');
  };

  const setManualInputRef = useCallback((element: HTMLInputElement | null) => {
    manualInputRef.current = element;
  }, []);

  const rows: WeightCheckParcelRow[] = useMemo(
    () =>
      parcels.map((parcel) => ({
        id: parcel.trackNumber,
        trackNumber: parcel.trackNumber,
        factWeight: parcel.factWeight,
        status: checks[parcel.trackNumber]?.status ?? 'notChecked',
        checkedWeight: checks[parcel.trackNumber]?.weight ?? null,
      })),
    [parcels, checks],
  );

  const checkedCount = rows.filter((row) => row.status === 'confirmed' || row.status === 'changed').length;

  const columns: Column<WeightCheckParcelRow>[] = useMemo(
    () => [
      {
        header: t('tableHeader.trackNumber'),
        accessor: 'trackNumber',
        renderCell: (row) => <span className="wc-code">{row.trackNumber}</span>,
        flex: 2,
        textAlign: 'center',
        minWidth: '160px',
      },
      {
        header: t('weightCheck.acceptanceWeight'),
        accessor: 'factWeight',
        renderCell: (row) => (row.factWeight ? `${row.factWeight.value} g` : '—'),
        textAlign: 'center',
        flex: 1,
        minWidth: '120px',
      },
      {
        header: t('weightCheck.checkedWeight'),
        accessor: 'checkedWeight',
        renderCell: (row) => (row.checkedWeight !== null ? `${row.checkedWeight} g` : '—'),
        textAlign: 'center',
        flex: 1,
        minWidth: '120px',
      },
      {
        header: t('weightCheck.checkStatus'),
        accessor: 'status',
        renderCell: (row) => (
          <span className={`wc-status wc-status--${row.status}`}>{t(`weightCheck.statuses.${row.status}`)}</span>
        ),
        textAlign: 'center',
        flex: 1,
        minWidth: '140px',
      },
    ],
    [t],
  );

  return (
    <FullscreenSheet height={'100dvh'} open={Boolean(bag)} onClose={close}>
      <FlexContainer direction="column" gap="16px">
        <FlexContainer justify="space-between" align="center" gap="16px">
          <FlexContainer direction="column" gap="4px">
            <Span fs={24} fw={600}>
              {t('weightCheck.bagTitle', { bagNumber: bag?.number ?? '' })}
            </Span>
            {scalesConnected !== null && (
              <Span fs={14} color={scalesConnected ? undefined : 'red'}>
                {scalesConnected
                  ? t('consolidatorTracking.changeWeight.scalesConnected')
                  : t('consolidatorTracking.changeWeight.scalesDisconnected')}
              </Span>
            )}
          </FlexContainer>
          <Button width="100px" disabled={isLoading} onClick={close}>
            {t('tableHeader.rollUp')}
          </Button>
        </FlexContainer>

        <ScannerInput
          label={t('weightCheck.scanLabel')}
          buttonLabel={t('consolidatorTracking.scan.scanButton')}
          isLoading={isLoading}
          disabled={isLoading || Boolean(manualTrackNumber)}
          maxValueLength={64}
          lang={i18n.language}
          isFocusFilter={Boolean(manualTrackNumber)}
          onSubmit={onScanSubmit}
        />

        {manualTrackNumber && (
          <div className="wc-manual">
            <div className="wc-warning">
              <TriangleAlertIcon color="red" />
              <span>{t('consolidatorTracking.changeWeight.manualWarning', { trackNumber: manualTrackNumber })}</span>
            </div>
            <InputText
              id="weight-check-manual"
              ref={setManualInputRef}
              type="number"
              textAlign="left"
              autoComplete="off"
              label={t('consolidatorTracking.manual.weightLabel')}
              disabled={isLoading}
              value={manualWeight}
              onChange={setManualWeight}
              onKeyDown={(event) => {
                if (event.key === 'Escape') cancelManual();
                if (event.key !== 'Enter') return;
                event.preventDefault();
                event.stopPropagation();
                void onManualSubmit();
              }}
            />
            <FlexContainer justify="space-between" align="center" gap="8px">
              <div className="wc-subhint">{t('consolidatorTracking.changeWeight.manualHint')}</div>
              <Button type="secondary" width="120px" disabled={isLoading} onClick={cancelManual}>
                {t('cancel')}
              </Button>
            </FlexContainer>
          </div>
        )}

        {lastResult && <div className={`wc-result wc-result--${lastResult.tone}`}>{lastResult.message}</div>}

        <div className="wc-card">
          <div className="wc-cardHead">
            <div className="wc-title">{t('weightCheck.bagParcels')}</div>
            <div className="wc-counter">
              {t('weightCheck.progress', { checked: checkedCount, total: rows.length })}
            </div>
          </div>
          <TableSimple<WeightCheckParcelRow>
            columns={columns}
            data={rows}
            enableColumnResizing={false}
            withOutPagination
            isLoading={isLoadingParcels}
            emptyPlaceholder={t('weightCheck.empty')}
            mobileWidth={680}
            maxHeightTable="calc(100dvh - 420px)"
          />
        </div>
      </FlexContainer>
    </FullscreenSheet>
  );
}
